import React, { createContext, ReactNode, useContext, useState } from "react";

import { BackgroundTask } from "@/util/interfaces/background/BackgroundTask";
import { BaseTask } from "@/util/interfaces/background/BaseTask";
import { PollingConfig } from "@/util/interfaces/background/PollingConfig";

export const UPLOAD_TASK = "Uploading files";
export const UPLOAD_TEAMS_TASK = "Uploading teams files";
export const PENDING_STATUS = "pending";
export const POLLING_STATUS = "polling";
export const COMPLETED_STATUS = "completed";
export const FAILED_STATUS = "failed";

interface BackgroundContextType {
  addBackgroundTask: (_tasks: BaseTask[], _taskType?: string) => string;
  getBackgroundTasks: () => BackgroundTask[];
  removeBackgroundTask: (_task_id: string) => void;
}
interface Status {
  status: "pending" | "completed" | "failed" | "polling";
}
const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

export const BackgroundProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [backgroundTasks, setBackgroundTasks] = useState<BackgroundTask[]>([]);

  const generateTaskId = (): string => {
    // Use crypto.randomUUID() for cryptographically secure random IDs
    const randomId = crypto.randomUUID().replaceAll("-", "").substring(0, 9);
    return `task_${Date.now()}_${randomId}`;
  };
  const removeBackgroundTask = (task_id: string) => {
    setBackgroundTasks((prevTasks) => prevTasks.filter((task) => task.id !== task_id));
  };

  const addBackgroundTask = (tasks: BaseTask[], taskType: string = UPLOAD_TASK): string => {
    const taskId = generateTaskId();
    tasks = tasks.map((task) => ({ ...task, id: generateTaskId() })); // Assign unique IDs to each BaseTask
    const newTask: BackgroundTask = {
      completed: false,
      id: taskId,
      type: taskType,
      tasks: tasks, // Copy BaseTasks into BackgroundTask with unique ids
    };
    setBackgroundTasks((prevTasks) => [...prevTasks, newTask]);
    for (const task of tasks) {
      const { promise, pollingConfig, id: subtask_id } = task;
      // Assign the task ID to each BaseTask
      // Configure interval to check every 5 seconds
      const intervalId = setInterval(() => {
        checkPromiseStatus(taskId, promise);
      }, 5000);

      // Update intervalId in the task
      task.intervalId = intervalId;

      // Add the task to the state

      // Handle promise resolution/rejection directly
      promise
        .then((result) => {
          // If polling is configured, check if needs polling with the task response
          if (pollingConfig?.shouldStartPolling(result)) {
            clearInterval(intervalId);
            startPolling(taskId, result, task.pollingConfig!, subtask_id);
          } else {
            updateTaskStatusWithResponse(taskId, COMPLETED_STATUS, result, subtask_id);
            clearInterval(intervalId);
          }
        })
        .catch(() => {
          updateTaskStatusWithResponse(taskId, FAILED_STATUS, null, subtask_id);
          clearInterval(intervalId);
        });
    }

    return taskId;
  };

  const startPolling = (
    taskId: string,
    initialResponse: any,
    pollingConfig: PollingConfig,
    subtask_id: string
  ) => {
    // Change status to polling and set the initial response
    updateTaskStatusWithResponse(taskId, POLLING_STATUS, initialResponse, subtask_id);
    const { pollingFunction, interval = 2000, maxAttempts = Infinity } = pollingConfig;

    let attempts = 0;

    const executePolling = async (): Promise<void> => {
      try {
        attempts++;
        console.log(`Polling attempt ${attempts} for task ${taskId}`);

        // Pass current response (initially from task, then from polling)
        const pollingResult = await pollingFunction(initialResponse);

        // Update polling response in the task
        updateTaskPollingResponse(taskId, pollingResult, subtask_id);

        // Check if needs to continue polling with the polling response
        if (pollingConfig.shouldContinuePolling(pollingResult)) {
          if (attempts >= maxAttempts) {
            throw new Error(`Polling max attempts (${maxAttempts}) reached`);
          }

          // Continue polling after the interval
          setTimeout(executePolling, interval);
        } else {
          // Polling completed - update final response
          updateTaskStatusWithResponse(taskId, COMPLETED_STATUS, initialResponse, subtask_id);
          console.log(`Polling completed for task ${taskId}`);
        }
      } catch (error) {
        updateTaskStatusWithResponse(taskId, FAILED_STATUS, initialResponse, subtask_id);
        console.error(`Polling failed for task ${taskId}:`, error);
      }
    };

    // Start polling after the initial interval
    setTimeout(executePolling, interval);
  };

  const checkPromiseStatus = async (taskId: string, promise: Promise<any>) => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), 0)
      );

      await Promise.race([promise, timeoutPromise]);
      console.log(`Background task ${taskId} completed successfully`);
    } catch (error) {
      if (error instanceof Error && error.message === "Timeout") {
        console.log(`Background task ${taskId} still in progress...`);
      } else {
        console.error(`Background task ${taskId} failed:`, error);
      }
    }
  };
  const updateSubTasksStatusWithResponse = (
    tasks: BaseTask[],
    subtask_id: string,
    status: Status["status"],
    response: any
  ) => {
    return tasks.map((proT) => (proT.id === subtask_id ? { ...proT, status, response } : proT));
  };
  const updateSubTasksPollingResponse = (
    tasks: BaseTask[],
    subtask_id: string,
    pollingResponse: any
  ) => {
    return tasks.map((proT) => (proT.id === subtask_id ? { ...proT, pollingResponse } : proT));
  };
  const updateTaskStatusWithResponse = (
    taskId: string,
    status: Status["status"],
    response: any,
    subtask_id: string
  ) => {
    setBackgroundTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? {
              ...task,
              tasks: updateSubTasksStatusWithResponse(task.tasks, subtask_id, status, response),
            }
          : task
      )
    );
  };

  const updateTaskPollingResponse = (taskId: string, pollingResponse: any, subtask_id: string) => {
    setBackgroundTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? {
              ...task,
              tasks: updateSubTasksPollingResponse(task.tasks, subtask_id, pollingResponse),
            }
          : task
      )
    );
  };

  const getBackgroundTasks = (): BackgroundTask[] => {
    return backgroundTasks;
  };

  const contextValue = React.useMemo(
    () => ({
      addBackgroundTask,
      getBackgroundTasks,
      removeBackgroundTask,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backgroundTasks]
  );
  return <BackgroundContext.Provider value={contextValue}>{children}</BackgroundContext.Provider>;
};

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error("useBackground must be used inside BackgroundProvider");
  }
  return context;
};
